import "katex/dist/katex.min.css";
import "./styles/main.css";
import Phaser from "phaser";
import { h, render } from "preact";
import { VIEW_H, VIEW_W } from "./config";
import "./math"; // registers question generators
import { BattleScene } from "./scenes/BattleScene";
import { BootScene } from "./scenes/BootScene";
import { PreloadScene } from "./scenes/PreloadScene";
import { TitleScene } from "./scenes/TitleScene";
import { WorldScene } from "./scenes/WorldScene";
import { battle } from "./battle/BattleController";
import { fetchCloudUser } from "./services/cloud";
import { saveGame } from "./state/save";
import { hero, screen, touch } from "./state/store";
import { App } from "./ui/App";

/**
 * Big screens get a whole-number zoom (every pixel crisp). Small screens — phones and portrait
 * tablets, where a whole-number zoom would leave most of the screen empty or overflow it — get
 * quarter steps down to 0.5 so the world still fills the width.
 */
function gameZoom(): number {
  const fit = Math.min(window.innerWidth / VIEW_W, window.innerHeight / VIEW_H);
  if (fit >= 2) return Math.floor(fit);
  return Math.max(0.5, Math.floor(fit * 4) / 4);
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: "game",
  width: VIEW_W,
  height: VIEW_H,
  pixelArt: true,
  roundPixels: true,
  backgroundColor: "#1b2a34",
  physics: {
    default: "arcade",
    arcade: { debug: false },
  },
  // Integer zoom keeps every pixel crisp (no half-pixel smearing of tiles, sprites or text).
  scale: {
    mode: Phaser.Scale.NONE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    zoom: gameZoom(),
  },
  // `?timeout=1` drives the loop with setTimeout for automated checks in embedded browsers where
  // requestAnimationFrame does not fire (opt-in, harmless for players).
  fps: { forceSetTimeOut: new URLSearchParams(location.search).has("timeout") },
  scene: [BootScene, PreloadScene, TitleScene, WorldScene, BattleScene],
};

export const game = new Phaser.Game(config);
window.addEventListener("resize", () => game.scale.setZoom(gameZoom()));

const uiRoot = document.getElementById("ui");
if (uiRoot) render(h(App, null), uiRoot);

// Who is logged in (Azure Static Web Apps auth); silently "nobody" outside the platform.
void fetchCloudUser();

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden" && screen.value === "world") saveGame();
});

// Dev-only hook for driving the game from the browser console / automated checks.
if (import.meta.env.DEV) {
  Object.assign(window, { __mathrpg: { game, touch, hero, battle, screen } });
}
